import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api.js';
import Spinner from '../components/Spinner.jsx';

const STATUS_LABEL = { offen: 'Offen', vermittelt: 'Vermittelt', geubt: 'Geübt', geprueft: 'Geprüft' };
const CAT_LABEL    = { theorie: 'Theorie', praxis_trocken: 'Trockenpraxis', ausbildungstauchgang: 'Ausbildungstauchgang' };

export default function Ausbildungsnachweis() {
  const { id } = useParams();
  const [items, setItems]     = useState([]);
  const [dives, setDives]     = useState([]);
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    Promise.all([
      api.getProgress(id),
      api.getDives(id),
    ]).then(([prog, diveData]) => {
      setItems(prog.items ?? []);
      setDives(Array.isArray(diveData) ? diveData : []);
      setStudent(prog.student ?? null);
    }).catch((e) => setError(e.message)).finally(() => setLoading(false));
  }, [id]);

  const byCategory = items.reduce((acc, item) => {
    (acc[item.category] = acc[item.category] ?? []).push(item);
    return acc;
  }, {});

  const geprueft = items.filter((i) => i.status === 'geprueft').length;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 print:hidden">
        <Link to={`/ausbilder/schueler/${id}`} className="text-sm text-ocean-600 hover:underline">← Zurück</Link>
        <h1 className="text-xl font-bold text-gray-900">Ausbildungsnachweis</h1>
        <button onClick={() => window.print()} className="btn-primary ml-auto text-sm print:hidden">
          <span aria-hidden="true">🖨</span> Drucken / PDF
        </button>
      </div>

      {loading && <div className="flex justify-center py-12"><Spinner size="lg" /></div>}

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">{error}</div>
      )}

      {!loading && (
        <div className="print:text-sm">
          {/* Kopfzeile */}
          <div className="card mb-4 print:border print:shadow-none">
            <h2 className="text-lg font-bold">Ausbildungsnachweis Feuerwehrtaucher</h2>
            <p className="text-sm text-gray-500">Feuerwehr Lüneburg — gem. FwDV 8</p>
            <div className="mt-3 grid grid-cols-2 gap-2 text-sm">
              <div><span className="text-gray-500">Schüler-Kürzel:</span> <strong>{student?.kuerzel ?? '—'}</strong></div>
              <div><span className="text-gray-500">Zielstufe:</span> <strong>Stufe {student?.stufe_ziel ?? '—'}</strong></div>
              <div><span className="text-gray-500">Geprüfte Lernziele:</span> <strong>{geprueft} / {items.length}</strong></div>
              <div><span className="text-gray-500">Tauchgänge gesamt:</span> <strong>{dives.length}</strong></div>
              <div><span className="text-gray-500">Ausgedruckt am:</span> <strong>{new Date().toLocaleDateString('de-DE')}</strong></div>
            </div>
          </div>

          {/* Lernziele je Kategorie */}
          {Object.entries(CAT_LABEL).map(([cat, label]) => {
            const catItems = byCategory[cat] ?? [];
            if (catItems.length === 0) return null;
            return (
              <div key={cat} className="mb-4">
                <h3 className="text-sm font-bold uppercase tracking-wide text-gray-600 mb-2">{label}</h3>
                <table className="w-full text-xs border-collapse">
                  <thead>
                    <tr className="bg-ocean-50">
                      <th className="text-left px-2 py-1 border border-ocean-200">Lernziel</th>
                      <th className="text-left px-2 py-1 border border-ocean-200 w-24">Status</th>
                      <th className="text-left px-2 py-1 border border-ocean-200 w-24">Datum</th>
                      <th className="text-left px-2 py-1 border border-ocean-200 w-28">Ausbilder</th>
                    </tr>
                  </thead>
                  <tbody>
                    {catItems.map((item) => (
                      <tr key={item.id} className="even:bg-gray-50">
                        <td className="px-2 py-1 border border-gray-200">{item.title}</td>
                        <td className="px-2 py-1 border border-gray-200 font-medium">
                          {STATUS_LABEL[item.status] ?? item.status}
                        </td>
                        <td className="px-2 py-1 border border-gray-200 text-gray-500">
                          {item.datum ?? ''}
                        </td>
                        <td className="px-2 py-1 border border-gray-200 text-gray-500">
                          {item.ausbilder ?? ''}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })}

          {/* Tauchgänge */}
          {dives.length > 0 && (
            <div className="mb-4">
              <h3 className="text-sm font-bold uppercase tracking-wide text-gray-600 mb-2">Tauchgänge</h3>
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className="bg-ocean-50">
                    <th className="text-left px-2 py-1 border border-ocean-200">#</th>
                    <th className="text-left px-2 py-1 border border-ocean-200">Datum</th>
                    <th className="text-left px-2 py-1 border border-ocean-200">Dauer</th>
                    <th className="text-left px-2 py-1 border border-ocean-200">Max. Tiefe</th>
                    <th className="text-left px-2 py-1 border border-ocean-200">Ort</th>
                    <th className="text-left px-2 py-1 border border-ocean-200">Bedingungen</th>
                  </tr>
                </thead>
                <tbody>
                  {dives.map((d, i) => (
                    <tr key={d.id} className="even:bg-gray-50">
                      <td className="px-2 py-1 border border-gray-200 text-gray-400 font-mono">{i + 1}</td>
                      <td className="px-2 py-1 border border-gray-200">{d.datum}</td>
                      <td className="px-2 py-1 border border-gray-200">{d.dauer_min} min</td>
                      <td className="px-2 py-1 border border-gray-200">{d.max_tiefe} m</td>
                      <td className="px-2 py-1 border border-gray-200">{d.ort ?? '—'}</td>
                      <td className="px-2 py-1 border border-gray-200">{d.bedingungen ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Unterschrift */}
          <div className="mt-8 grid grid-cols-2 gap-8 text-xs text-gray-500">
            <div className="border-t border-gray-300 pt-2">Unterschrift Ausbilder / TEF</div>
            <div className="border-t border-gray-300 pt-2">Unterschrift Schüler</div>
          </div>
        </div>
      )}
    </div>
  );
}
